export const MEAL_ALLOWED_ROLES = Object.freeze([1, 2, 10]);

export const MEAL_INITIAL_FILTERS = Object.freeze({
  status: "",
  priority: "",
  type: "",
  conditionActive: "",
  dateFrom: "",
  dateTo: "",
});

export const MEAL_STATUS_LABELS = Object.freeze({
  PENDIENTE: "Pendiente",
  EN_REVISION: "En revisión",
  VALIDADA: "Validada",
  DESCARTADA: "Descartada",
});

export const MEAL_PRIORITY_LABELS = Object.freeze({
  HIGH: "Alta",
  MEDIUM: "Media",
  LOW: "Baja",
  INFO: "Informativa",
});

export const MEAL_TYPE_LABELS = Object.freeze({
  DATOS_FALTANTES: "Datos faltantes",
  MENOR_SIN_REPRESENTANTE: "Menor sin representante",
  RECONSULTA_PROBABLE: "Reconsulta probable",
  COMENTARIO_ALERTA: "Comentario con alerta",
});

export const MEAL_EVENT_LABELS = Object.freeze({
  DETECTED: "Detectada",
  ASSIGNED: "Asignada",
  REVIEW_STARTED: "Revisión iniciada",
  REVALIDATED: "Revalidada",
  VALIDATED: "Validada",
  DISCARDED: "Descartada",
});

export const MEAL_RULE_SOURCE_LABELS = Object.freeze({
  BUILT_IN: "Regla estándar del sistema",
  PROJECT_RULE: "Regla específica del proyecto",
});

export const MEAL_RESOLUTION_LABELS = Object.freeze({
  INFORMACION_CONFIRMADA: "Información confirmada",
  RECONSULTA_CONFIRMADA: "Reconsulta confirmada",
  DUPLICADO_CONFIRMADO: "Duplicado confirmado",
  IDENTIDADES_DISTINTAS: "Identidades distintas",
  SIN_INCIDENCIA: "Sin incidencia",
  DATO_COMPLETADO: "Dato completado",
  ERROR_TRANSCRIPCION: "Error de transcripción",
  ERROR_CORREGIDO: "Error corregido",
  CORREGIDO_Y_REVALIDADO: "Corregido y revalidado",
  OTRO: "Otro",
});

export const MEAL_VALIDATION_RESOLUTIONS = Object.freeze([
  "INFORMACION_CONFIRMADA",
  "RECONSULTA_CONFIRMADA",
  "DUPLICADO_CONFIRMADO",
  "IDENTIDADES_DISTINTAS",
  "SIN_INCIDENCIA",
  "DATO_COMPLETADO",
  "ERROR_TRANSCRIPCION",
  "ERROR_CORREGIDO",
  "CORREGIDO_Y_REVALIDADO",
  "OTRO",
]);

export const MEAL_CORRECTION_RESOLUTIONS = Object.freeze([
  "DATO_COMPLETADO",
  "ERROR_TRANSCRIPCION",
  "ERROR_CORREGIDO",
  "CORREGIDO_Y_REVALIDADO",
]);

export const getStoredMealUser = () => {
  try {
    return JSON.parse(localStorage.getItem("token")) || null;
  } catch (error) {
    return null;
  }
};

export const canAccessMealIncidents = (user) =>
  MEAL_ALLOWED_ROLES.includes(Number(user?.rol));

export const humanizeMealCode = (value) => {
  const code = String(value || "").trim();
  if (!code) return "Sin información";

  return code
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
    )
    .join(" ");
};

export const getMealTypeLabel = (type) =>
  MEAL_TYPE_LABELS[type] || humanizeMealCode(type);

export const getMealStatusLabel = (status) =>
  MEAL_STATUS_LABELS[status] || humanizeMealCode(status);

export const getMealPriorityLabel = (priority) =>
  MEAL_PRIORITY_LABELS[priority] || humanizeMealCode(priority);

export const getMealEventLabel = (eventType) =>
  MEAL_EVENT_LABELS[eventType] || humanizeMealCode(eventType);

export const getMealRuleSourceLabel = (source) =>
  MEAL_RULE_SOURCE_LABELS[source] || humanizeMealCode(source);

export const getMealProjectId = (project) =>
  project?.id ?? project?.projectId ?? project?.idProyecto ?? project?.idproyectos ?? "";

export const getMealProjectLabel = (project) => {
  const code = project?.code ?? project?.codigo ?? project?.codigoProyecto ?? "";
  const name =
    project?.name ?? project?.nombre ?? project?.projectName ?? project?.nombreProyecto ?? "Proyecto";
  return [code, name].filter(Boolean).join(" · ");
};

export const toMealBoolean = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const formatMealDateTime = (value) => {
  if (!value) return "Sin información";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("es-VE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const readStateValue = (byState, aliases) => {
  const expectedStates = Array.isArray(aliases) ? aliases : [aliases];
  if (Array.isArray(byState)) {
    const item = byState.find(
      (entry) => expectedStates.includes(String(entry.status || entry.state || entry.id || ""))
    );
    return Number(item?.total ?? item?.count ?? item?.value ?? 0) || 0;
  }
  const value = expectedStates.find((state) => byState?.[state] !== undefined);
  return Number(value ? byState[value] : 0) || 0;
};

export const getMealSummaryCards = (summary = {}) => {
  const byState = summary.byState || summary.porEstado || {};
  const states = {
    PENDIENTE: readStateValue(byState, ["pending", "PENDIENTE"]) || Number(summary.pending) || 0,
    EN_REVISION: readStateValue(byState, ["inReview", "EN_REVISION"]) || Number(summary.inReview) || 0,
    VALIDADA: readStateValue(byState, ["validated", "VALIDADA"]) || Number(summary.validated) || 0,
    DESCARTADA: readStateValue(byState, ["discarded", "DESCARTADA"]) || Number(summary.discarded) || 0,
  };
  const calculatedTotal = Object.values(states).reduce((total, value) => total + value, 0);

  return [
    { key: "total", label: "Total", value: Number(summary.totalCases ?? summary.total ?? calculatedTotal) || 0 },
    { key: "PENDIENTE", label: "Pendientes", value: states.PENDIENTE },
    { key: "EN_REVISION", label: "En revisión", value: states.EN_REVISION },
    { key: "VALIDADA", label: "Validadas", value: states.VALIDADA },
    { key: "DESCARTADA", label: "Descartadas", value: states.DESCARTADA },
  ];
};

export const getMealTypeOptions = (incidents = [], summary = {}) => {
  const values = new Set(incidents.map((incident) => incident?.type).filter(Boolean));
  const byType = summary.byType || {};

  if (Array.isArray(byType)) {
    byType.forEach((item) => values.add(item.type || item.id || item.code));
  } else {
    Object.keys(byType).forEach((type) => values.add(type));
  }

  return [...values]
    .filter(Boolean)
    .sort((left, right) => getMealTypeLabel(left).localeCompare(getMealTypeLabel(right)));
};

export const buildMealIncidentParams = ({ projectId, filters, page, pageSize }) => {
  const params = { projectId, page, pageSize };
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) params[key] = value;
  });
  return params;
};

const compactMealParams = (params = {}) =>
  Object.entries(params).reduce((result, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) result[key] = value;
    return result;
  }, {});

export const buildMealControlExportParams = ({ projectId, filters = {} }) =>
  compactMealParams({
    projectId,
    status: filters.status,
    priority: filters.priority,
    type: filters.type,
    assignedTo: filters.assignedTo,
    conditionActive: filters.conditionActive,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });

export const buildMealClosureExportParams = ({ projectId, filters = {} }) =>
  compactMealParams({
    projectId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });

export const hasMealFilters = (filters = {}) =>
  Object.values(filters).some((value) => value !== "" && value !== null && value !== undefined);

const ERROR_MESSAGES = Object.freeze({
  PROJECT_REQUIRED: "Selecciona un proyecto para buscar incidencias.",
  INVALID_FILTER: "Revisa los filtros seleccionados.",
  INVALID_RESOLUTION: "La resolución seleccionada no es válida para esta incidencia.",
  COMMENT_REQUIRED: "Debes escribir un comentario para continuar.",
  AUTH_REQUIRED: "Debes iniciar sesión nuevamente.",
  AUTH_INVALID: "Tu sesión no es válida o ha expirado.",
  FORBIDDEN: "No tienes permisos para realizar esta acción.",
  ACTOR_NOT_ALLOWED: "No tienes permisos para realizar esta acción.",
  INCIDENT_NOT_FOUND: "El recurso solicitado no está disponible.",
  ASSIGNEE_NOT_FOUND: "La persona responsable no está disponible.",
  RESOURCE_NOT_FOUND: "El recurso solicitado no está disponible.",
  INVALID_STATE: "La incidencia cambió de estado. Actualiza la información e intenta nuevamente.",
  INCIDENT_NOT_ASSIGNED: "La incidencia debe estar asignada antes de continuar.",
  CONDITION_STILL_ACTIVE:
    "La condición todavía está presente. Después de corregir el dato fuente, vuelve a revalidar la incidencia antes de cerrarla como corregida.",
  ANALYSIS_LIMIT_EXCEEDED: "El proyecto contiene más registros de los permitidos para este análisis.",
  ANALYSIS_BUSY: "El motor MEAL está ocupado. Intenta nuevamente en unos momentos.",
  ANALYSIS_ALREADY_RUNNING: "Ya hay un análisis en curso para este proyecto.",
  FINGERPRINT_SECRET_REQUIRED: "La sincronización MEAL no está disponible temporalmente.",
  INVALID_EXPORT_FILE: "No fue posible generar el archivo de exportación.",
  INTERNAL_ERROR: "No fue posible completar la solicitud. Intenta nuevamente.",
});

export const getMealErrorCode = (error) =>
  error?.response?.data?.error?.code ||
  error?.response?.data?.code ||
  error?.response?.data?.errorCode ||
  error?.code ||
  "";

export const getMealErrorMessage = (error) => {
  const code = getMealErrorCode(error);
  if (ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  if (!error?.response) return "No fue posible conectar con el servidor.";
  if (error.response.status === 401) return ERROR_MESSAGES.AUTH_INVALID;
  if (error.response.status === 403) return ERROR_MESSAGES.FORBIDDEN;
  if (error.response.status === 404) return ERROR_MESSAGES.RESOURCE_NOT_FOUND;
  return ERROR_MESSAGES.INTERNAL_ERROR;
};

export const getMealResolutionOptions = (incident = {}) => {
  const backendOptions =
    incident.allowedResolutionTypes || incident.resolutionTypes || incident.allowedResolutions;
  const values = Array.isArray(backendOptions)
    ? backendOptions.map((option) =>
        typeof option === "string" ? { value: option, label: MEAL_RESOLUTION_LABELS[option] || humanizeMealCode(option) } : {
          value: option.value || option.code || option.id,
          label:
            option.label ||
            option.name ||
            MEAL_RESOLUTION_LABELS[option.value || option.code || option.id] ||
            humanizeMealCode(option.value || option.code || option.id),
        }
      )
    : [];

  if (values.length > 0) return values.filter((option) => option.value);
  return MEAL_VALIDATION_RESOLUTIONS.map((value) => ({
    value,
    label: MEAL_RESOLUTION_LABELS[value],
  }));
};

export const normalizeMealPagination = (pagination = {}, fallbackPageSize = 20) => ({
  page: Math.max(1, Number(pagination.page) || 1),
  pageSize: Math.max(1, Number(pagination.pageSize) || fallbackPageSize),
  total: Math.max(0, Number(pagination.total) || 0),
  totalPages: Math.max(0, Number(pagination.totalPages) || 0),
});

const toMealCount = (value) => Math.max(0, Number(value) || 0);

export const normalizeMealAnalysisResult = (result = {}) => ({
  projectId: result.projectId ?? null,
  rowsAnalyzed: toMealCount(result.rowsAnalyzed),
  candidatesPersisted: toMealCount(result.candidatesPersisted),
  created: toMealCount(result.created),
  observed: toMealCount(result.observed),
  conditionCleared: toMealCount(result.conditionCleared),
  reopened: toMealCount(result.reopened),
  reconciliationApplied: result.reconciliationApplied === true,
  shadowNotPersisted: toMealCount(result.shadowNotPersisted),
  measured: {
    missingDocuments: toMealCount(result.measured?.missingDocuments),
    invalidDocuments: toMealCount(result.measured?.invalidDocuments),
    missingBirthDates: toMealCount(result.measured?.missingBirthDates),
  },
});

export const MEAL_XLSX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const getMealHeader = (headers, name) => {
  if (!headers) return "";
  if (typeof headers.get === "function") return headers.get(name) || "";
  const key = Object.keys(headers).find((header) => header.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : "";
};

const decodeMealFilename = (value) => {
  const normalized = String(value || "").trim().replace(/^['"]|['"]$/g, "");
  try {
    return decodeURIComponent(normalized);
  } catch (error) {
    return normalized;
  }
};

const toSafeMealFilename = (value) => {
  const basename = String(value || "").replace(/\\/g, "/").split("/").pop();
  const withoutControls = Array.from(String(basename || ""))
    .map((character) => {
      const code = character.charCodeAt(0);
      return code < 32 || code === 127 ? "_" : character;
    })
    .join("");
  const safeName = withoutControls.replace(/[<>:"/\\|?*]/g, "_").trim();
  return safeName && safeName !== "." && safeName !== ".." && /\.xlsx$/i.test(safeName)
    ? safeName
    : "";
};

const toLocalISODate = (date = new Date()) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return toLocalISODate(new Date());
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildMealExportFallbackFilename = (kind, projectId, date = new Date()) => {
  const prefix = kind === "closure" ? "MEAL_Cierre" : "MEAL_Incidencias";
  const safeProjectId = String(projectId || "Proyecto").replace(/[^a-zA-Z0-9_-]/g, "_");
  return `${prefix}_Proyecto_${safeProjectId}_${toLocalISODate(date)}.xlsx`;
};

export const getMealExportFilename = (headers, fallbackFilename) => {
  const disposition = getMealHeader(headers, "content-disposition");
  const extendedMatch = disposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (extendedMatch) {
    const filename = toSafeMealFilename(decodeMealFilename(extendedMatch[1]));
    if (filename) return filename;
  }

  const regularMatch = disposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  if (regularMatch) {
    const filename = toSafeMealFilename(decodeMealFilename(regularMatch[1] || regularMatch[2]));
    if (filename) return filename;
  }

  return fallbackFilename;
};

export const normalizeMealExportResponse = (response, { kind, projectId, date } = {}) => {
  const blob = response?.data;
  const headerType = getMealHeader(response?.headers, "content-type");
  const contentType = String(headerType || blob?.type || "").split(";", 1)[0].trim().toLowerCase();

  if (!(blob instanceof Blob) || blob.size <= 0 || contentType !== MEAL_XLSX_CONTENT_TYPE) {
    const error = new Error("Invalid MEAL export response");
    error.code = "INVALID_EXPORT_FILE";
    throw error;
  }

  const fallbackFilename = buildMealExportFallbackFilename(kind, projectId, date);
  return {
    blob,
    filename: getMealExportFilename(response.headers, fallbackFilename),
  };
};

export const downloadMealExport = ({ blob, filename }) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  try {
    link.href = objectUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(objectUrl);
  }
};

const parseMealBlobPayload = async (blob) => {
  if (!(blob instanceof Blob)) return null;
  try {
    const text = typeof blob.text === "function"
      ? await blob.text()
      : await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error);
          reader.readAsText(blob);
        });
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
};

export const getMealExportErrorMessage = async (error) => {
  const blobPayload = await parseMealBlobPayload(error?.response?.data);
  if (!blobPayload) return getMealErrorMessage(error);
  return getMealErrorMessage({
    ...error,
    response: { ...error.response, data: blobPayload },
  });
};
